import axiosInstance from '../config/axios';
import { 
  GeneralSettings, 
  DatabaseBackupSettings, 
  NotificationSettings, 
  ExchangeRateSettings,
  SmtpSettings
} from '../types/settings';

/**
 * API yanıt tipi
 */
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * Genel ayarlar servisi
 */
class SettingsService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  }

  /**
   * Tüm genel ayarları getirir
   */
  async getGeneralSettings(): Promise<GeneralSettings> {
    try {
      const response = await axiosInstance.get<ApiResponse<GeneralSettings>>(
        `/api/v1/settings/general`
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Ayarlar alınamadı');
    } catch (error) {
      console.error('Genel ayarlar alınırken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * Genel ayarları günceller
   */
  async updateGeneralSettings(settings: GeneralSettings): Promise<boolean> {
    try {
      const response = await axiosInstance.put<ApiResponse<boolean>>(
        `/api/v1/settings/general`,
        settings
      );
      
      return response.data.success;
    } catch (error) {
      console.error('Genel ayarlar güncellenirken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * Veritabanı yedekleme ayarlarını getirir
   */
  async getDatabaseBackupSettings(): Promise<DatabaseBackupSettings> {
    try {
      const response = await axiosInstance.get<ApiResponse<DatabaseBackupSettings>>(
        `/api/v1/settings/database-backup`
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Veritabanı yedekleme ayarları alınamadı');
    } catch (error) {
      console.error('Veritabanı yedekleme ayarları alınırken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * Veritabanı yedekleme ayarlarını günceller
   */
  async updateDatabaseBackupSettings(settings: DatabaseBackupSettings): Promise<boolean> {
    try {
      const response = await axiosInstance.put<ApiResponse<boolean>>(
        `/api/v1/settings/database-backup`,
        settings
      );
      
      return response.data.success;
    } catch (error) {
      console.error('Veritabanı yedekleme ayarları güncellenirken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * Manuel veritabanı yedeklemesi başlatır
   */
  async startManualBackup(databaseNames: string[]): Promise<ApiResponse<string>> {
    try {
      const response = await axiosInstance.post<ApiResponse<string>>(
        `/api/v1/database/backup`,
        { databaseNames }
      );
      
      return response.data;
    } catch (error) {
      console.error('Manuel yedekleme başlatılırken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * Bildirim ayarlarını getirir
   * NOT: Bu fonksiyon artık kullanılmıyor. NotificationSettingsTab bileşeni 
   * doğrudan notificationService.getNotificationPreferences() kullanıyor.
   */
  async getNotificationSettings(): Promise<NotificationSettings> {
    try {
      // Varsayılan değerler döndür - gerçek veriler notificationService'den alınıyor
      return {
        enablePushNotifications: false,
        enableEmailNotifications: false,
        enableSmsNotifications: false,
        notifyOnBackupComplete: false,
        notifyOnBackupError: false,
        notifyOnExchangeRateUpdate: false,
        notifyOnSystemErrors: false,
        notifyOnNewCustomers: false,
        notifyOnLowStock: false,
        notifyOnPaymentReceived: false,
        emailRecipients: [],
        smsRecipients: []
      };
    } catch (error) {
      console.error('Bildirim ayarları alınırken hata oluştu:', error);
      // Varsayılan değerler döndür
      return {
        enablePushNotifications: false,
        enableEmailNotifications: false,
        enableSmsNotifications: false,
        notifyOnBackupComplete: false,
        notifyOnBackupError: false,
        notifyOnExchangeRateUpdate: false,
        notifyOnSystemErrors: false,
        notifyOnNewCustomers: false,
        notifyOnLowStock: false,
        notifyOnPaymentReceived: false,
        emailRecipients: [],
        smsRecipients: []
      };
    }
  }

  /**
   * Bildirim ayarlarını günceller
   * NOT: Bu fonksiyon artık kullanılmıyor. NotificationSettingsTab bileşeni 
   * doğrudan notificationService.updateNotificationPreferences() kullanıyor.
   */
  async updateNotificationSettings(settings: NotificationSettings): Promise<boolean> {
    try {
      console.warn('updateNotificationSettings fonksiyonu kullanımdan kaldırıldı. notificationService.updateNotificationPreferences() kullanın.');
      return false;
    } catch (error) {
      console.error('Bildirim ayarları güncellenirken hata oluştu:', error);
      return false;
    }
  }

  /**
   * Döviz kuru ayarlarını getirir
   */
  async getExchangeRateSettings(): Promise<ExchangeRateSettings> {
    try {
      const response = await axiosInstance.get<ApiResponse<ExchangeRateSettings>>(
        `/api/v1/settings/exchange-rates`
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Döviz kuru ayarları alınamadı');
    } catch (error) {
      console.error('Döviz kuru ayarları alınırken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * Döviz kuru ayarlarını günceller
   */
  async updateExchangeRateSettings(settings: ExchangeRateSettings): Promise<boolean> {
    try {
      const response = await axiosInstance.put<ApiResponse<boolean>>(
        `/api/v1/settings/exchange-rates`,
        settings
      );
      
      return response.data.success;
    } catch (error) {
      console.error('Döviz kuru ayarları güncellenirken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * Manuel döviz kuru güncellemesi başlatır
   */
  async updateExchangeRatesManually(): Promise<ApiResponse<boolean>> {
    try {
      const response = await axiosInstance.post<ApiResponse<boolean>>(
        `/api/v1/exchange-rates/update`,
        {}
      );
      
      return response.data;
    } catch (error) {
      console.error('Manuel döviz kuru güncellemesi başlatılırken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * SMTP ayarlarını getirir
   */
  async getSmtpSettings(): Promise<SmtpSettings> {
    try {
      const response = await axiosInstance.get<SmtpSettings>(`/api/v1/settings/smtp`);
      return response.data;
    } catch (error) {
      console.error('SMTP ayarları alınırken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * SMTP ayarlarını günceller
   */
  async updateSmtpSettings(settings: SmtpSettings): Promise<ApiResponse<boolean>> {
    try {
      const response = await axiosInstance.put<ApiResponse<boolean>>(`/api/v1/settings/smtp`, settings);
      return response.data;
    } catch (error) {
      console.error('SMTP ayarları güncellenirken hata oluştu:', error);
      throw error;
    }
  }

  /**
   * SMTP ayarlarıyla test e-postası gönderir
   */
  async testSmtpSettings(settings: SmtpSettings): Promise<ApiResponse<string>> {
    try {
      const response = await axiosInstance.post<ApiResponse<string>>(`/api/v1/settings/smtp/test`, settings);
      return response.data as any;
    } catch (error) {
      console.error('SMTP test e-postası gönderilirken hata oluştu:', error);
      // Backend 400 dönerse de kullanıcıya mesaj gösterebilmek için hatayı tekrar fırlatıyoruz
      throw error;
    }
  }
}

export default new SettingsService();
